import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  downloadTransaction,
  getTransactions,
} from "../../services/transactionService";
import toast from "react-hot-toast";
import Pagination from "../../components/Pagination";
import ProfileNav from "../../components/ProfileNav";
import * as XLSX from "xlsx";
import { getTenantFromUrl } from "../../helpers/http";

const Transactions = () => {
  const getCurrentDate = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Function to get the date a month ago in the format "YYYY-MM-DD"
  const getDateAMonthAgo = () => {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() - 1);
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const [transactions, setTransactions] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [scheme, setScheme] = useState(null);
  const [localScheme, setLocalScheme] = useState("");
  const [localOrderId, setLocalOrderId] = useState("");
  const [localRetailerId, setLocalRetailerId] = useState("");
  const [localTerminalId, setLocalTerminalId] = useState("");

  const [status, setStatus] = useState(null);
  const [retailerId, setRetailerId] = useState(null);
  const [terminalId, setTerminalId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const itemsPerPage = 100;

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const tenant = getTenantFromUrl();
      const res = await getTransactions(currentPage, itemsPerPage, {
        startDate,
        endDate,
        scheme,
        status,
        orderId,
        retailerId,
        terminalId,
      }, tenant);
      setTransactions(res.data?.result);
      setTotalCount(res.data?.count);
      setLoading(false);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
        setLoading(false);
      }
    }
  }, [
    endDate,
    scheme,
    startDate,
    status,
    orderId,
    retailerId,
    terminalId,
    currentPage,
  ]);

  const reset = useCallback(async () => {
    setLoading(true);
    try {
      const tenant = getTenantFromUrl();
      const res = await getTransactions(currentPage, itemsPerPage, {
        startDate: null,
        endDate: null,
        scheme: null,
        status: null,
        orderId: null,
        retailerId: null,
        terminalId: null,
      }, tenant);
      setTransactions(res.data?.result);
      setTotalCount(res.data?.count);
      setStartDate("");
      setEndDate("");
      setLocalScheme("");
      setLocalOrderId("");
      setLocalRetailerId("");
      setStatus("");
      setLocalTerminalId("");
      setLoading(false);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
        setLoading(false);
      }
    }
  }, [currentPage]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Logic to get current items based on pagination
  // const indexOfLastItem = currentPage * itemsPerPage;
  // const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // const currentItems = transactions.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const updateScheme = useRef(
    debounce((value) => {
      setScheme(value || null);
    }, 1000),
    []
  );

  const updateOrderId = useRef(
    debounce((value) => {
      setOrderId(value || null);
    }, 1000),
    []
  );

  const updateRetailerId = useRef(
    debounce((value) => {
      setRetailerId(value || null);
    }, 1000),
    []
  );

  const updateTerminalId = useRef(
    debounce((value) => {
      setTerminalId(value || null);
    }, 1000),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value.trim();
    setLocalScheme(e.target.value);
    updateScheme.current(value || null);
  };

  const handleSearchOrderId = (e) => {
    const value = e.target.value.trim();
    setLocalOrderId(e.target.value);
    updateOrderId.current(value || null);
  };

  const handleSearchRetailerId = (e) => {
    const value = e.target.value.trim();
    setLocalRetailerId(e.target.value);
    updateRetailerId.current(value || null);
  };

  const handleSearchTerminalId = (e) => {
    const value = e.target.value.trim();
    setLocalTerminalId(e.target.value);
    updateTerminalId.current(value || null);
  };

  // const handleDownload = () => {
  //   // Flatten the object and format the date column
  //   const rows = transactions.map((info) => ({
  //     id: info.id,
  //     description: info.description,
  //     customerName: info.customerName,
  //     pan: info.pan,
  //     scheme: info.scheme,
  //     amount: info.amount,
  //     // Format the date directly in the data
  //     date: new Date(info.initiated).toLocaleDateString("en-US", {
  //       year: "numeric",
  //       month: "long",
  //       day: "numeric",
  //     }),
  //     approvalCode: info.approvalCode,
  //     status: info.status,
  //   }));

  //   // Create workbook
  //   const workbook = XLSX.utils.book_new();

  //   // Create the worksheet from the data
  //   const worksheet = XLSX.utils.json_to_sheet(rows);

  //   // Customize header names
  //   XLSX.utils.sheet_add_aoa(
  //     worksheet,
  //     [
  //       [
  //         "ID",
  //         "Description",
  //         "Customer Name",
  //         "PAN",
  //         "Scheme",
  //         "Amount",
  //         "Date",
  //         "Approval Code",
  //         "Status",
  //       ],
  //     ],
  //     { origin: "A1" }
  //   );

  //   // Append the worksheet to the workbook
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

  //   // Adjust column widths (optional)
  //   const colWidths = [
  //     { wch: 10 }, // ID
  //     { wch: 300 }, // Description
  //     { wch: 20 }, // Customer Name
  //     { wch: 20 }, // PAN
  //     { wch: 25 }, // Scheme
  //     { wch: 15 }, // Amount
  //     { wch: 20 }, // Date
  //     { wch: 20 }, // Approval Code
  //     { wch: 10 }, // Status
  //   ];
  //   worksheet["!cols"] = colWidths;

  //   // Write the file without compression
  //   XLSX.writeFile(workbook, "Report.xlsx");
  // };

  const handleDownload = async () => {
    setExportLoading(true);
    try {
      const blobData = await downloadTransaction({
        startDate,
        endDate,
        scheme,
        status,
        orderId,
        retailerId,
        terminalId,
      });

      // Get the content type from the response headers
      const contentType = blobData.type;
      // Create a blob from the response data
      const blob = new Blob([blobData], { type: contentType });
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      // Create a temporary <a> element
      const link = document.createElement("a");
      link.href = url;
      // Set the filename
      link.download = "Transaction Report";
      // Simulate a click on the link to initiate the download
      link.click();
      // Cleanup
      window.URL.revokeObjectURL(url);
      setExportLoading(false);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
        setExportLoading(false);
      }
    }
  };

  return (
    <div className="h-screen">
      <article className="h-screen flex gap-4">
        <section className="w-[48%] flex-grow-[6] pt-4 ">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-navy-blue">
              Reports - Transaction
            </h1>
            <div>
              <ProfileNav />
            </div>
          </div>

          <div className="bg-white w-full mt-4 rounded-xl shadow-sm overflow-x-auto">
            {/* TOP TOOLBAR: Selected / Reset / Export (left) --- Pagination (right) */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-3">
                {/* Selected count (optional) */}
                <p className="text-sm text-gray-600 border-r pr-4">
                  1 Selected
                </p>

                {/* Reset */}
                <button
                  type="button"
                  onClick={reset}
                  className="h-10 px-3 rounded-md bg-white border text-sm font-medium text-gray-800 hover:bg-gray-50 transition"
                  aria-label="Reset filters"
                >
                  Reset
                </button>

                {/* Export */}
                {!loading && (
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={loading}
                    className={`h-10 px-4 rounded-md text-sm font-medium inline-flex items-center gap-2 transition ${
                      exportLoading
                        ? "bg-orange-300 text-white cursor-not-allowed opacity-70"
                        : "bg-text-orange text-white hover:opacity-95"
                    }`}
                    aria-label="Export filtered transactions"
                  >
                    {exportLoading ? (
                      <svg
                        className="animate-spin h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 3v12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 11l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21 21H3"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    <span>{exportLoading ? "Exporting..." : "Export"}</span>
                  </button>
                )}
              </div>

              {/* Pagination (top-right) */}
              <div>
                <Pagination
                  totalItems={totalCount}
                  itemsPerPage={itemsPerPage}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </div>
            </div>

            {/* TABLE (column titles + filter row + body) */}
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                {/* Column titles */}
                <tr className="text-left border-b text-gray-600 text-xs uppercase tracking-wide">
                  <th className="px-3 py-2">Transaction ID</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Customer Name</th>
                  <th className="px-3 py-2">Masked PAN</th>
                  <th className="px-3 py-2">Scheme</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Retailer ID</th>
                  <th className="px-3 py-2">Terminal ID</th>
                  <th className="px-3 py-2">Status</th>
                </tr>

                {/* Filter row */}
                <tr className="border-b bg-white text-gray-700">
                  <th className="px-3 py-2">
                    <input
                      type="text"
                      placeholder="Search ID"
                      onChange={handleSearchOrderId}
                      value={localOrderId}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                  </th>
                  <th></th>
                  <th></th>
                  <th></th>

                  <th className="px-3 py-2">
                    <input
                      type="text"
                      placeholder="Search scheme"
                      value={localScheme}
                      onChange={handleSearchChange}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                  </th>
                  <th></th>

                  <th className="px-3 py-2 flex flex-col gap-1">
                    <input
                      type="date"
                      placeholder="Start Date"
                      onChange={(e) => setStartDate(e.target.value)}
                      value={startDate}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                    <input
                      type="date"
                      placeholder="End Date"
                      onChange={(e) => setEndDate(e.target.value)}
                      value={endDate}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                  </th>
                  <th className="px-3 py-2">
                    <input
                      type="text"
                      placeholder="Retailer ID"
                      onChange={handleSearchRetailerId}
                      value={localRetailerId}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                  </th>
                  <th className="px-3 py-2">
                    <input
                      type="text"
                      placeholder="Terminal ID"
                      onChange={handleSearchTerminalId}
                      value={localTerminalId}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                  </th>
                  <th className="px-3 py-2">
                    <select
                      onChange={(e) => {
                        const value = e.target.value;
                        setStatus(value === "" ? null : value);
                      }}
                      value={status ?? ""}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none"
                    >
                      <option value="" disabled>
                        All
                      </option>
                      <option value="APPROVED">Approved</option>
                      <option value="Initiated">Declined</option>
                    </select>
                  </th>
                </tr>
              </thead>

              {/* Body */}
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-6 text-gray-500">
                      Transaction Loading...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-6 text-gray-500">
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((t) => (
                    <tr
                      key={t.id}
                      className="hover:bg-orange-50 transition text-sm text-gray-700"
                    >
                      <td className="px-3 py-2">{t.id}</td>
                      <td
                        className="px-3 py-2 truncate max-w-[150px]"
                        title={t.description}
                      >
                        {t.description}
                      </td>
                      <td className="px-3 py-2">{t.customerName}</td>
                      <td className="px-3 py-2">{t.pan}</td>
                      <td className="px-3 py-2">{t.scheme}</td>
                      <td className="px-3 py-2 text-right">{t.amount}</td>
                      <td className="px-3 py-2">
                        {new Date(t.initiated).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </td>
                      <td className="px-3 py-2">{t.retailerId}</td>
                      <td className="px-3 py-2">{t.terminalId}</td>
                      <td className="px-3 py-2">{t.status}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </article>
      <style>
        {`
        /* Customize the label (the container) */
.container {
  display: block;
  position: relative;
  padding-left: 35px;
  cursor: pointer;
  font-size: 12px;
  color: rgba(28, 28, 28, 0.4);
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 13.5px;
  width: 13.5px;
  background-color: #eee;
  border-radius: 3px;
  margin-top:2px;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
  background-color: #ccc;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  background-color: #323232;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 6px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
        `}
      </style>
    </div>
  );
};

export default Transactions;
